// All requests go through /api which Vite proxies to the API service.

export const BASE = '/api/v1'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ── Core fetch wrapper ────────────────────────────────────────

// Promise-based lock: all concurrent 401s share the same refresh attempt
let refreshPromise: Promise<boolean> | null = null

export async function request<T>(path: string, options?: RequestInit, retry = true): Promise<T> {
  const token = localStorage.getItem('access_token')

  const res = await fetch(`${BASE}${path}`, {
    ...options,
    credentials: 'include',          // sends the httpOnly refresh cookie
    headers: {
      ...(options?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  })

  // Auto-refresh on 401 (expired access token)
  if (res.status === 401 && retry) {
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => { refreshPromise = null })
    }
    const refreshed = await refreshPromise

    if (refreshed) return request<T>(path, options, false)

    // Refresh failed — clear local state and signal logout
    localStorage.removeItem('access_token')
    localStorage.removeItem('auth_user')
    window.dispatchEvent(new Event('auth:expired'))
    throw new ApiError(401, 'Session expired')
  }

  if (res.status === 204) return undefined as T

  const data = await res.json().catch(() => null)
  if (!res.ok) {
    throw new ApiError(res.status, data?.message ?? res.statusText, data?.details)
  }
  return data as T
}

export async function refreshAccessToken(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method:      'POST',
      credentials: 'include',
    })
    if (!res.ok) return false
    const data = await res.json()
    localStorage.setItem('access_token', data.accessToken)
    return true
  } catch {
    return false
  }
}
